import { isEqual } from "lodash";
import type {
  CriticalInfrastructureImpact,
  DefenseCapacityImpact,
  ImpactAnalysisData,
  InternationalObligationsImpact,
  LegalRepresentativeData,
} from "../types/impact-analysis";
import type { ContactData } from "../types/contact";
import type { ClassificationResult } from "./nis2-classifier";
import type { EntityData } from "../types/entity";
import { DEFAULT_IMPACT_ANALYSIS } from "../constants/analysis";
import { DEFAULT_CONTACT_DATA } from "../constants/contact";
import { calculateImpactAnalysisResult } from "./disruptiveEffectCalculator";
import { computeClassification } from "./classificationCalculations";
import { evaluateEntitySize } from "./size";

export type PreparedUpdates = {
  impact_analysis?: ImpactAnalysisData;
  contact_data?: ContactData;
  nis_classification?: ClassificationResult | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function migrateOldImpactData(data: any): ImpactAnalysisData {
  if (!data) return DEFAULT_IMPACT_ANALYSIS;

  // Check if already in new format
  if (
    data.section1_mandatory &&
    data.section2_public_impact &&
    data.section3_systemic_risk
  ) {
    // Convert boolean values to string types if needed (backward compatibility)
    const migrated = { ...data } as ImpactAnalysisData;

    // Convert affects_critical_infrastructure
    if (
      typeof migrated.section2_public_impact?.national_economy
        ?.affects_critical_infrastructure === "boolean"
    ) {
      migrated.section2_public_impact.national_economy.affects_critical_infrastructure =
        migrated.section2_public_impact.national_economy
          .affects_critical_infrastructure === true
          ? "affected"
          : "no";
    }

    // Convert affects_defense_capacity
    if (
      typeof migrated.section2_public_impact?.defense_security
        ?.affects_defense_capacity === "boolean"
    ) {
      migrated.section2_public_impact.defense_security.affects_defense_capacity =
        migrated.section2_public_impact.defense_security
          .affects_defense_capacity === true
          ? "affected"
          : "no";
    }

    // Convert affects_international_obligations
    if (
      typeof migrated.section3_systemic_risk
        ?.affects_international_obligations === "boolean"
    ) {
      migrated.section3_systemic_risk.affects_international_obligations =
        migrated.section3_systemic_risk.affects_international_obligations ===
        true
          ? "affected"
          : "no";
    }

    return migrated;
  }

  // Check if old structure (has section1_unique_provider or section3_public_impact)
  if (
    data.section1_unique_provider ||
    data.section2_critical_importance ||
    data.section3_public_impact
  ) {
    const oldNationalEconomy =
      data.section3_public_impact?.national_economy || {};
    const oldHealthLife = data.section3_public_impact?.health_life || {};
    const oldDefenseSecurity =
      data.section3_public_impact?.defense_security || {};
    const oldSystemicRisk = data.section4_systemic_risk || {};

    const newFormat = {
      section1_mandatory: {
        is_sole_essential_provider:
          data.section1_unique_provider?.anexa1_unique ||
          data.section1_unique_provider?.anexa2_unique ||
          data.section1_unique_provider?.is_sole_essential_provider ||
          null,
        is_sole_essential_provider_explanation: null,
        formally_designated:
          data.section2_critical_importance?.formally_designated || null,
        formally_designated_explanation: null,
      },
      section2_public_impact: {
        fundamental_rights: {
          personal_data_affected:
            data.section3_public_impact?.fundamental_rights
              ?.personal_data_affected || null,
          personal_data_affected_explanation: null,
          public_service_access_lost:
            data.section3_public_impact?.fundamental_rights
              ?.public_service_access_lost || null,
          public_service_access_lost_explanation: null,
        },
        national_economy: {
          gdp_loss_over_threshold:
            oldNationalEconomy.gdp_loss_over_threshold || null,
          gdp_loss_over_threshold_explanation: null,
          affects_other_sector_services: null,
          affects_other_sector_services_explanation: null,
          highest_sector_impact_percentage: null,
          highest_sector_impact_percentage_explanation: null,
          affects_critical_infrastructure:
            oldNationalEconomy.supports_critical_infrastructure === true
              ? ("affected" as CriticalInfrastructureImpact)
              : oldNationalEconomy.supports_critical_infrastructure === false
                ? ("no" as CriticalInfrastructureImpact)
                : null,
          affects_critical_infrastructure_explanation: null,
        },
        health_life: {
          can_cause_death: null,
          can_cause_death_explanation: null,
          chronic_illness_or_disability:
            oldHealthLife.deaths_chronic_illness || null,
          chronic_illness_or_disability_explanation: null,
          traumatic_injuries_or_health_impact:
            oldHealthLife.traumatic_injuries || null,
          traumatic_injuries_or_health_impact_explanation: null,
        },
        financial_impact: {
          people_financial_loss:
            data.section3_public_impact?.financial_impact
              ?.people_financial_loss || null,
          people_financial_loss_explanation: null,
        },
        defense_security: {
          affects_defense_capacity:
            oldDefenseSecurity.affects_state_capacity === true
              ? ("affected" as DefenseCapacityImpact)
              : oldDefenseSecurity.affects_state_capacity === false
                ? ("no" as DefenseCapacityImpact)
                : null,
          affects_defense_capacity_explanation: null,
          generates_state_incapacity: null,
          generates_state_incapacity_explanation: null,
          damages_state_reputation: null,
          damages_state_reputation_explanation: null,
        },
      },
      section3_systemic_risk: {
        affects_other_sectors: oldSystemicRisk.affects_other_sectors || null,
        affects_other_sectors_explanation: null,
        affected_sectors_count: null,
        highest_impact_percentage:
          oldSystemicRisk.affected_sectors_percentage || null,
        highest_impact_percentage_explanation: null,
        affects_international_obligations:
          oldSystemicRisk.affects_international_obligations === true
            ? ("affected" as InternationalObligationsImpact)
            : oldSystemicRisk.affects_international_obligations === false
              ? ("no" as InternationalObligationsImpact)
              : null,
        affects_international_obligations_explanation: null,
      },
    };
    return {
      ...newFormat,
      result: calculateImpactAnalysisResult(newFormat),
    };
  }
  // Handle onboarding-simplified format: only { result: { article9a, ... } } without sections
  if (
    data.result &&
    !data.section1_mandatory &&
    !data.section2_public_impact &&
    !data.section3_systemic_risk
  ) {
    const result = data.result;
    return {
      ...DEFAULT_IMPACT_ANALYSIS,
      section1_mandatory: {
        ...DEFAULT_IMPACT_ANALYSIS.section1_mandatory,
        is_sole_essential_provider: result.article9a ?? null,
        formally_designated: result.article9d ?? null,
      },
      result,
    };
  }

  console.error(
    "migrateOldImpactData: unrecognized impact_analysis format",
    data
  );
  return DEFAULT_IMPACT_ANALYSIS;
}

export function mergeWithDefaultImpactData(
  partial: Partial<ImpactAnalysisData>
): ImpactAnalysisData {
  const s1 = partial.section1_mandatory;
  const s2 = partial.section2_public_impact;
  const s3 = partial.section3_systemic_risk;
  const d = DEFAULT_IMPACT_ANALYSIS;

  return {
    section1_mandatory: {
      ...d.section1_mandatory,
      ...s1,
    },
    section2_public_impact: {
      fundamental_rights: {
        ...d.section2_public_impact.fundamental_rights,
        ...s2?.fundamental_rights,
      },
      national_economy: {
        ...d.section2_public_impact.national_economy,
        ...s2?.national_economy,
      },
      health_life: {
        ...d.section2_public_impact.health_life,
        ...s2?.health_life,
      },
      financial_impact: {
        ...d.section2_public_impact.financial_impact,
        ...s2?.financial_impact,
      },
      defense_security: {
        ...d.section2_public_impact.defense_security,
        ...s2?.defense_security,
      },
    },
    section3_systemic_risk: {
      ...d.section3_systemic_risk,
      ...s3,
    },
    ...(partial.result ? { result: partial.result } : {}),
  };
}

export function prepareImpactAnalysisUpdates(
  analysis: ImpactAnalysisData,
  repData: LegalRepresentativeData,
  existingImpactAnalysis: ImpactAnalysisData | undefined,
  existingContactData: ContactData | null,
  entityData?: EntityData | null
): PreparedUpdates | null {
  const existingReprezentantLegal = existingContactData?.reprezentantLegal;

  const updatedContactData: ContactData = {
    ...(existingContactData || DEFAULT_CONTACT_DATA),
    reprezentantLegal: {
      nume: repData.nume,
      prenume: repData.prenume,
      functie: repData.functia,
      email: existingReprezentantLegal?.email || "",
      telefon: existingReprezentantLegal?.telefon || "",
    },
  };

  const impactAnalysisChanged = !isEqual(analysis, existingImpactAnalysis);
  const contactDataChanged = !isEqual(updatedContactData, existingContactData);

  if (!impactAnalysisChanged && !contactDataChanged) {
    return null;
  }

  const updates: PreparedUpdates = {};

  if (impactAnalysisChanged) {
    const newResult = calculateImpactAnalysisResult(analysis);
    updates.impact_analysis = { ...analysis, result: newResult };

    if (entityData) {
      const sizeEvaluation = evaluateEntitySize(
        entityData.nr_salariati ?? 0,
        entityData.cifra_afaceri ?? 0,
        entityData.active ?? 0
      );
      // article9Enabled is always true here: this function is only called
      // from the ef-perturbator page, which requires Art.9 to apply
      updates.nis_classification = computeClassification(
        sizeEvaluation,
        entityData.selected_services ?? [],
        true,
        newResult
      );
    } else {
      updates.nis_classification = null;
    }
  }

  if (contactDataChanged) {
    updates.contact_data = updatedContactData;
  }

  return updates;
}
